import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, GraduationCap, FileSpreadsheet, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import StudentFormDialog from "../components/StudentFormDialog";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/rbac";
import { localName, localFullName } from "../lib/localName";

export default function StudentsPage() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const canEdit = can.editStudents(user);
  const canImport = can.importStudents(user);
  const canSeeParents = can.editParents(user);
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [classes, setClasses] = useState([]);
  const [items, setItems] = useState([]);
  const [school, setSchool] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      setSchool(prev => {
        if (prev) return prev;
        if (!isSuper && user?.school_id && list.some(s => s.id === user.school_id)) return user.school_id;
        if (list.length >= 1) return list[0].id;
        return prev;
      });
    });
  }, [user?.school_id, isSuper]);
  useEffect(() => {
    if (school) {
      api.get(`/grades?school_id=${school}`).then(r => setGrades(r.data || []));
      api.get(`/classes?school_id=${school}`).then(r => {
        const list = r.data || [];
        setClasses(list);
        // Auto-select first class when switching schools or on first load
        setFilterClass(prev => {
          if (prev && list.some(c => c.id === prev)) return prev;
          return list[0]?.id || "";
        });
      });
    } else {
      setClasses([]);
      setFilterClass("");
    }
  }, [school]);
  const load = () => {
    let q = `/students?`;
    if (school) q += `school_id=${school}&`;
    if (filterClass) q += `class_id=${filterClass}&`;
    api.get(q).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); }, [school, filterClass]);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setOpen(true); };
  const remove = async (it) => { await api.delete(`/students/${it.id}`); toast.success(t("success")); load(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <h1 className="text-3xl font-semibold">{t("students")}</h1>
        <div className="flex gap-3 flex-wrap">
          <Select value={school} onValueChange={setSchool} disabled={!isSuper}>
            <SelectTrigger className="w-56" data-testid="students-school-select"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
            <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="w-48" data-testid="students-class-select"><SelectValue placeholder={t("select_class")} /></SelectTrigger>
            <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => nav("/parents")} data-testid="goto-parents-btn" hidden={!canSeeParents} style={!canSeeParents ? {display:"none"} : undefined}>
            <Users className="w-4 h-4 me-2" />{t("parents")}
          </Button>
          <Button variant="outline" onClick={() => nav("/import/students")} data-testid="goto-import-btn" hidden={!canImport} style={!canImport ? {display:"none"} : undefined}>
            <FileSpreadsheet className="w-4 h-4 me-2" />{t("import_from_excel")}
          </Button>
          {canEdit && <>
            <Button onClick={openNew} disabled={!school || classes.length === 0} data-testid="new-student-btn"><Plus className="w-4 h-4 me-2" />{t("new_student")}</Button>
            <StudentFormDialog open={open} onOpenChange={setOpen} schoolId={school}
                               grades={grades} classes={classes} editing={editing} onSaved={load} />
          </>}
        </div>
      </div>

      {/* Desktop: table */}
      <Card className="border border-border shadow-none overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-start px-4 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("name")}</th>
                <th className="text-start px-4 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("classes")}</th>
                <th className="text-start px-4 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("national_id_label")}</th>
                <th className="text-end px-4 py-3 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s, i) => (
                <tr
                  key={s.id}
                  data-testid={`student-${s.id}`}
                  className={`border-t border-border ${i % 2 ? "bg-muted/20" : ""} hover:bg-primary/5 cursor-pointer transition`}
                  onClick={() => nav(`/students/${s.id}`)}
                >
                  <td className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary grid place-items-center overflow-hidden">
                      {s.photo_url
                        ? <img src={s.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s.photo_url}` : s.photo_url} alt="" className="w-full h-full object-cover" />
                        : <GraduationCap className="w-4 h-4" />}
                    </div>
                    <span className="hover:text-primary">{localFullName(s, lang)}</span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{classes.find(c => c.id === s.class_id)?.name || "-"}</td>
                  <td className="px-4 py-3 font-mono text-muted-foreground">{s.national_id || "-"}</td>
                  <td className="px-4 py-3 text-end" onClick={(e) => e.stopPropagation()}>{canEdit ? <RowActions item={s} onEdit={openEdit} onDelete={remove} testIdPrefix="student" /> : null}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">{t("no_data")}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Mobile: stacked cards */}
      <div className="md:hidden space-y-2.5">
        {items.map((s) => (
          <Card
            key={s.id}
            data-testid={`student-card-${s.id}`}
            onClick={() => nav(`/students/${s.id}`)}
            className="p-3 border border-border/60 rounded-2xl shadow-sm touch-card flex items-center gap-3"
          >
            <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center overflow-hidden shrink-0">
              {s.photo_url
                ? <img src={s.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s.photo_url}` : s.photo_url} alt="" className="w-full h-full object-cover" />
                : <GraduationCap className="w-5 h-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{localFullName(s, lang)}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                <span className="truncate">{classes.find(c => c.id === s.class_id)?.name || "—"}</span>
                {s.national_id && <span className="font-mono">· {s.national_id}</span>}
              </div>
            </div>
            {canEdit && <div onClick={(e) => e.stopPropagation()} className="shrink-0"><RowActions item={s} onEdit={openEdit} onDelete={remove} testIdPrefix="student-m" /></div>}
          </Card>
        ))}
        {items.length === 0 && <Card className="p-10 text-center text-muted-foreground border-dashed rounded-2xl">{t("no_data")}</Card>}
      </div>
    </div>
  );
}
